// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@ces/XDL/PreStyle/breakpoints";
import systemZIndex from "@ces/XDL/PreStyle/zIndex";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";

function pickMultiple<T extends {}, Exclude extends keyof T>(object: T, keys: Readonly<Exclude[]>): Pick<T, Exclude> {
    const result: any = { ...object };
    Object.keys(object).forEach((key: any) => {
        if (!keys.includes(key)) {
            delete result[key];
        }
    });
    return result;
}

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To check if JS event listener is support on browser
 * @param eventName
 */
export const isEventSupported = (eventName: string) => {
    let el: any = document.createElement("div");
    eventName = `on${eventName}`;
    let isSupported = eventName in el;
    if (!isSupported) {
        el.setAttribute(eventName, "return;");
        isSupported = typeof el[eventName] === "function";
    }
    el = null;
    return isSupported;
};

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

export const xGetFulfillmentLabel = (state: ApplicationState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, pickUpWarehouse } = getSession(state);
    const shipTo = getCurrentCartState(state).value?.defaultWarehouse;

    let addressLabel = translate("Ship");
    let phone = shipTo?.phone;
    if (fulfillmentMethod === FulfillmentMethod.Ship && (isAuthenticated || rememberMe) && !isGuest && shipTo) {
        addressLabel = `${shipTo.description}, ${shipTo.address1}, ${shipTo.city}, ${shipTo.state || ""} ${
            shipTo.postalCode
        }`;
    } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
        addressLabel = translate(
            "Pick up {0}",
            pickUpWarehouse ? `at ${pickUpWarehouse.description || pickUpWarehouse.name}` : "",
        );
        phone = pickUpWarehouse?.phone;
    }

    return { addressLabel, phone };
};

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!opts) {
        values[maxWidths[0]] = {
            slidesPerView: 1,
        };
        values[maxWidths[1]] = {
            slidesPerView: 2,
        };
        values[maxWidths[2]] = {
            slidesPerView: 3,
        };
        values[maxWidths[3]] = {
            slidesPerView: 4,
        };
        return values;
    }

    Object.keys(opts).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +opts[+key],
        };
    });
    return values;
};

// Product List Table helpers
export const productListTableViewOmitKeys = ["valueId", "productsById"];
export const productListTableViewIsLeafLevel = (level: Dictionary<string>) => {
    return Object.keys(level).length === productListTableViewOmitKeys.length;
};

export const levelDelimiter = "þ";
export const colonDelimiter = "ý";

export const getOrderNumber = (erpOrderNumber: string, webOrderNumber: string) => {
    const trimmedErpNumber = erpOrderNumber && erpOrderNumber.trim();
    return trimmedErpNumber || webOrderNumber;
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );

    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const safeJsonParse = (str: string) => {
    try {
        return JSON.parse(str);
    } catch {
        return str;
    }
};

export const isTouchDevice = () => {
    if (typeof window === "undefined") {
        return false;
    }
    return !!window.ontouchstart || navigator?.maxTouchPoints > 0 || (navigator as any)?.msMaxTouchPoints > 0;
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};

export const delay = (time = 100) => {
    return new Promise(resolve => setTimeout(() => resolve(true), time));
};
